import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, movieTags, regionTags, yearOptions } from '@/mocks/categories';
import { getMoviesByCategory, allMovies } from '@/mocks/movies';

const categoryNames: Record<string, string> = {
  movie: '电影',
  tv: '电视剧',
  anime: '动漫',
  variety: '综艺',
};

export default function CategoryPage() {
  const { slug = 'movie' } = useParams<{ slug: string }>();
  const [activeTag, setActiveTag] = useState<string>('全部');
  const [activeRegion, setActiveRegion] = useState<string>('全部');
  const [activeYear, setActiveYear] = useState<string>('全部');

  const currentCat = categories.find(c => c.slug === slug);
  const catName = categoryNames[slug] || slug;

  useEffect(() => {
    setActiveTag('全部');
    setActiveRegion('全部');
    setActiveYear('全部');
  }, [slug]);

  let movies = getMoviesByCategory(slug);

  if (activeTag !== '全部') {
    movies = movies.filter(m => m.tags.includes(activeTag));
  }
  if (activeRegion !== '全部') {
    movies = movies.filter(m => m.region === activeRegion);
  }
  if (activeYear !== '全部') {
    if (activeYear.includes('-')) {
      const [start, end] = activeYear.includes('以前') ? [0, parseInt(activeYear)] : activeYear.split('-').map(Number);
      if (activeYear.includes('以前')) {
        movies = movies.filter(m => parseInt(m.year) <= end);
      } else {
        movies = movies.filter(m => {
          const y = parseInt(m.year);
          return y >= start && y <= end;
        });
      }
    } else {
      movies = movies.filter(m => m.year === activeYear);
    }
  }

  const allTags = ['全部', ...movieTags];
  const allRegions = ['全部', ...new Set(allMovies.filter(m => m.category === slug).map(m => m.region))];
  const availableYears = ['全部', ...yearOptions.filter(y => {
    return movies.some(m => {
      if (y.includes('-') || y.includes('以前')) return true;
      return m.year === y;
    });
  })];

  const categoryPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `红桃影院 - ${catName}分类`,
    description: `红桃影院${catName}分类页面，提供海量高清${catName}免费在线观看，每日更新最新${catName}资源。`,
    url: `https://www.hongtao.tv/category/${slug}`,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categoryPageSchema) }}
      />
      <title>红桃影院{catName} - 高清{catName}在线观看 | 免费{catName}资源</title>
      <meta name="description" content={`红桃影院${catName}分类，海量高清${catName}免费在线观看。最新${catName}每日更新，支持多端流畅播放，无广告打扰，随时随地畅享优质${catName}体验。`} />
      <link rel="canonical" href={`https://www.hongtao.tv/category/${slug}`} />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[
              { label: '首页', to: '/' },
              { label: catName },
            ]} />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                {catName}
              </h1>
              <p className="text-foreground-500 text-sm">
                共找到 <strong className="text-primary-600">{movies.length}</strong> 部{catName}资源
              </p>
            </div>

            <div className="flex items-center gap-3 mb-4">
              {categories.map(cat => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="space-y-3 mb-8">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-400 font-medium whitespace-nowrap">类型：</span>
                {allTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => setActiveTag(tag)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeTag === tag
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-400 font-medium whitespace-nowrap">地区：</span>
                {allRegions.map(region => (
                  <button
                    key={region}
                    onClick={() => setActiveRegion(region)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeRegion === region
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {region}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-400 font-medium whitespace-nowrap">年代：</span>
                {availableYears.map(year => (
                  <button
                    key={year}
                    onClick={() => setActiveYear(year)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeYear === year
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>

            {movies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5" data-product-shop>
                {movies.map(movie => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                  <i className="ri-emotion-sad-line text-foreground-300 text-2xl"></i>
                </div>
                <p className="text-foreground-500">暂无符合条件的{catName}资源</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}